import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../../../../../../config/api";

function UploadNew({
    language,
    currentUser,
    file,
    setFile,
    status,
    setStatus,
    loading,
    setLoading,
    selectedCategory,
    onBack,
}) {
    const navigate = useNavigate();

    // Progress Steps Component
    const ProgressSteps = ({ currentStep, language }) => {
        const steps = [
            {
                id: 1,
                labelEn: "Upload PDF",
                labelZh: "上傳PDF",
                icon: (
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                    </svg>
                ),
            },
            {
                id: 2,
                labelEn: "Review & Edit",
                labelZh: "審核編輯",
                icon: (
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                    </svg>
                ),
            },
            {
                id: 3,
                labelEn: "Complete",
                labelZh: "完成",
                icon: (
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                        />
                    </svg>
                ),
            },
        ];

        return (
            <div className="mb-8">
                <div className="flex items-center justify-between relative">
                    {/* Progress Line Background */}
                    <div
                        className="absolute top-1/3 left-5 w-11/12 h-1 bg-gray-200"
                        style={{ zIndex: 0 }}
                    />
                    {/* Active Progress Line */}
                    <div
                        className="absolute top-1/3 left-5 h-1 bg-orange-500 transition-all duration-500"
                        style={{
                            width: `${
                                ((currentStep - 1) / (steps.length - 1)) * 100
                            }%`,
                            zIndex: 0,
                        }}
                    />

                    {steps.map((step, index) => {
                        const isCompleted = currentStep > step.id;
                        const isActive = currentStep === step.id;

                        return (
                            <div
                                key={step.id}
                                className="flex flex-col items-center relative"
                                style={{ zIndex: 1 }}
                            >
                                {/* Step Circle */}
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 mb-2 ${
                                        isCompleted
                                            ? "bg-orange-500 text-white shadow-lg shadow-orange-500/50"
                                            : isActive
                                              ? "bg-orange-500 text-white shadow-lg shadow-orange-500/50 scale-110"
                                              : "bg-white border-2 border-gray-300 text-gray-400"
                                    }`}
                                >
                                    {isCompleted ? (
                                        <svg
                                            className="w-5 h-5"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    ) : (
                                        step.icon
                                    )}
                                </div>
                                {/* Step Label */}
                                <p
                                    className={`text-xs font-medium text-center whitespace-nowrap transition-colors ${
                                        isActive
                                            ? "text-orange-500"
                                            : isCompleted
                                              ? "text-gray-900"
                                              : "text-gray-400"
                                    }`}
                                >
                                    {language === "zh"
                                        ? step.labelZh
                                        : step.labelEn}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setStatus("");
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && droppedFile.type === "application/pdf") {
            setFile(droppedFile);
            setStatus("");
        }
    };

    // ── Duplicate-check modal state ──────────────────────────────────────────
    const [duplicateModal, setDuplicateModal] = useState({
        open: false,
        existingProduct: null,
        extractedData: null, // Document AI extraction result kept for navigation
        versionCount: 0, // how many versions already exist
    });

    // ── Language code state for version naming ───────────────────────────────
    const [availableLangCodes, setAvailableLangCodes] = useState([]);
    const [selectedLangCode, setSelectedLangCode] = useState("");

    useEffect(() => {
        const fetchLangCodes = async () => {
            if (!currentUser) return;
            try {
                const token = await currentUser.getIdToken();
                const res = await axios.get(
                    `${getApiUrl()}/api/v2/products/lang-codes`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    },
                );
                if (res.data?.data) {
                    setAvailableLangCodes(res.data.data);
                    console.log("Fetched language codes:", res.data);
                    // No auto-select — leave blank so the user must choose manually
                }
            } catch (err) {
                console.warn("Failed to fetch lang codes:", err);
            }
        };
        fetchLangCodes();
    }, [currentUser]);

    const handleUpload = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (!file || !currentUser) {
            setLoading(false);
            return alert("No file selected or user not logged in.");
        }

        const formData = new FormData();
        formData.append("pdf", file);

        try {
            const token = await currentUser.getIdToken();

            const res = await axios.post(
                `${getApiUrl()}/pdf/check-document-ai`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            console.log("Upload response:", res.data);
            const extracted = res.data.extracted_data.data;

            // ── Early duplicate check ────────────────────────────────────────
            const resolvedCategoryId =
                selectedCategory?.subSubCategoryId ??
                selectedCategory?.subCategoryId ??
                selectedCategory?.categoryId;
            const modelId = extracted.model_id || "";

            if (resolvedCategoryId && modelId) {
                try {
                    const checkRes = await axios.post(
                        `${getApiUrl()}/api/v2/products/check-exists`,
                        {
                            category_id: resolvedCategoryId,
                            model_id: modelId,
                            lang_code: selectedLangCode || null,
                        },
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        },
                    );

                    if (checkRes.data?.data?.exists) {
                        // Product already exists — show modal instead of navigating
                        setDuplicateModal({
                            open: true,
                            existingProduct: checkRes.data.data.product,
                            extractedData: extracted,
                            versionCount: checkRes.data.data.version_count || 0,
                            versionSummary:
                                checkRes.data.data.version_summary || {},
                        });
                        return; // stop here; modal will handle next step
                    }
                } catch (checkErr) {
                    // If the check fails, just proceed — ProductProperties has
                    // its own fallback duplicate check.
                    console.warn("Early duplicate check failed:", checkErr);
                }
            }

            // No duplicate — navigate directly
            navigateToProperties(extracted, false);
        } catch (error) {
            console.error("Upload error:", error);
            const errorMessage =
                error.response?.data?.error || error.message || "Unknown error";
            alert(
                language === "zh"
                    ? `上傳錯誤: ${errorMessage}`
                    : `Upload error: ${errorMessage}`,
            );
        } finally {
            setLoading(false);
        }
    };

    /** Navigate to ProductProperties with the extracted data. */
    const navigateToProperties = (
        extracted,
        duplicateChecked,
        existingProduct = null,
        versionLabel = null,
    ) => {
        navigate(
            {
                pathname: "/product-properties",
                search: `?lang=${language}`,
            },
            {
                state: {
                    productCategory: selectedCategory || {},
                    productName: extracted.model_id || "",
                    productDescription: extracted.description || "",
                    attributes: extracted.attributes || [],
                    file: file,
                    reachCompliant:
                        extracted.compliance?.reach_compliant ?? false,
                    rohsCompliant:
                        extracted.compliance?.rohs_compliant ?? false,
                    ulComparable: extracted.compliance?.ul_comparable ?? false,
                    // Flags for ProductProperties to know the duplicate status
                    duplicateChecked,
                    existingProduct,
                    versionLabel, // e.g. "en-2"
                    selectedLangCode, // carry over the language selection
                },
            },
        );
    };

    /** User chose "New Version" in the duplicate modal. */
    const handleNewVersion = () => {
        const { extractedData, existingProduct } = duplicateModal;
        // No longer build version label here — backend auto-generates it
        setDuplicateModal({
            open: false,
            existingProduct: null,
            extractedData: null,
            versionCount: 0,
        });
        navigateToProperties(
            extractedData,
            true,
            existingProduct,
            null, // version label will be auto-generated by backend
        );
    };

    /** User chose "Cancel" in the duplicate modal. */
    const handleCancelDuplicate = () => {
        setDuplicateModal({
            open: false,
            existingProduct: null,
            extractedData: null,
            versionCount: 0,
        });
    };

    return (
        <div className="flex justify-center">
            <div className="w-full max-w-2xl">
                {/* Back to Upload Menu */}
                {onBack && (
                    <button
                        onClick={onBack}
                        className="mb-6 inline-flex items-center gap-2 text-gray-600 dark:text-gray-100 hover:text-orange-500 dark:hover:text-orange-500 transition-colors duration-200 font-medium"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        {language === "zh"
                            ? "返回上傳選單"
                            : "Back to Upload Menu"}
                    </button>
                )}

                {/* Progress Tracker */}
                <ProgressSteps currentStep={1} language={language} />

                {/* Main Upload Card */}
                <div className="bg-white rounded-2xl border border-gray-200 p-8 relative overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-orange-500/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                    {/* Header */}
                    <div className="relative mb-8 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/10 rounded-2xl mb-4">
                            <svg
                                className="w-8 h-8 text-orange-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {language === "zh"
                                ? "上傳產品PDF"
                                : "Upload Product PDF"}
                        </h2>
                        <p className="text-gray-600">
                            {language === "zh"
                                ? "上傳PDF文件以創建或更新產品信息"
                                : "Upload a PDF file to create or update product information"}
                        </p>
                    </div>

                    {/* Loading Overlay */}
                    {loading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 backdrop-blur-sm z-10 rounded-2xl">
                            <div className="relative mb-4">
                                <div className="w-16 h-16 border-4 border-orange-500/30 rounded-full" />
                                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                            <p className="text-gray-900 font-medium">
                                {language === "zh"
                                    ? "處理中..."
                                    : "Processing..."}
                            </p>
                            <p className="text-gray-600 text-sm mt-1">
                                {language === "zh"
                                    ? "正在分析您的PDF文件"
                                    : "Analyzing your PDF file"}
                            </p>
                        </div>
                    )}

                    <div className="relative space-y-6">
                        {/* Drop Zone */}
                        <div
                            className={`relative border-2 border-dashed rounded-2xl p-10 text-center transition-all duration-300 cursor-pointer ${
                                file
                                    ? "border-orange-500/50 bg-orange-500/5"
                                    : "border-gray-300 hover:border-orange-500/50 hover:bg-gray-50"
                            }`}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() =>
                                document.getElementById("pdf-upload").click()
                            }
                        >
                            {/* Animated upload icon */}
                            <div
                                className={`mb-4 inline-flex items-center justify-center w-16 h-16 rounded-2xl transition-all duration-300 ${
                                    file ? "bg-orange-500/20" : "bg-gray-100"
                                }`}
                            >
                                {file ? (
                                    <svg
                                        className="w-8 h-8 text-orange-500"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        className="w-8 h-8 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                    </svg>
                                )}
                            </div>

                            <div className="text-gray-700 mb-4">
                                {file ? (
                                    <span className="text-orange-500 font-medium">
                                        {language === "zh"
                                            ? "文件已選擇"
                                            : "File selected"}
                                    </span>
                                ) : (
                                    <>
                                        <span className="text-orange-500 font-medium">
                                            {language === "zh"
                                                ? "點擊上傳"
                                                : "Click to upload"}
                                        </span>{" "}
                                        {language === "zh"
                                            ? "或拖放文件到此處"
                                            : "or drag and drop"}
                                    </>
                                )}
                            </div>

                            <p className="text-gray-500 text-sm">
                                {language === "zh"
                                    ? "僅支持 PDF 格式"
                                    : "PDF files only"}
                            </p>

                            <input
                                id="pdf-upload"
                                type="file"
                                accept="application/pdf"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </div>

                        {/* Selected File Info */}
                        {file && (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 animate-fadeIn">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center">
                                            <svg
                                                className="w-6 h-6 text-red-500"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-gray-900 font-medium truncate max-w-[200px] sm:max-w-[300px]">
                                                {file.name}
                                            </p>
                                            <p className="text-gray-600 text-sm">
                                                {(
                                                    file.size /
                                                    1024 /
                                                    1024
                                                ).toFixed(2)}{" "}
                                                MB
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFile(null);
                                        }}
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Upload Button */}
                        <button
                            className={`w-full py-4 px-6 rounded-xl font-medium text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                                file
                                    ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:-translate-y-0.5"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                            onClick={handleUpload}
                            disabled={!file || loading}
                        >
                            {loading ? (
                                <>
                                    <svg
                                        className="w-5 h-5 animate-spin"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                    {language === "zh"
                                        ? "處理中..."
                                        : "Processing..."}
                                </>
                            ) : (
                                <>
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                        />
                                    </svg>
                                    {language === "zh"
                                        ? "上傳並處理"
                                        : "Upload & Process"}
                                </>
                            )}
                        </button>

                        {/* Status Message */}
                        {status && (
                            <div
                                className={`p-4 rounded-xl text-center animate-fadeIn ${
                                    status.includes("success") ||
                                    status.includes("成功")
                                        ? "bg-green-500/10 border border-green-500/30 text-green-400"
                                        : "bg-red-500/10 border border-red-500/30 text-red-400"
                                }`}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    {status.includes("success") ||
                                    status.includes("成功") ? (
                                        <svg
                                            className="w-5 h-5"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            className="w-5 h-5"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    )}
                                    <p className="font-medium">{status}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Help Section */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                            <svg
                                className="w-5 h-5 text-blue-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                        </div>
                        <h4 className="text-gray-900 font-medium text-sm mb-1">
                            {language === "zh"
                                ? "支持的格式"
                                : "Supported Format"}
                        </h4>
                        <p className="text-gray-600 text-xs">PDF</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                            <svg
                                className="w-5 h-5 text-green-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 10V3L4 14h7v7l9-11h-7z"
                                />
                            </svg>
                        </div>
                        <h4 className="text-gray-900 font-medium text-sm mb-1">
                            {language === "zh" ? "自動提取" : "Auto-Extract"}
                        </h4>
                        <p className="text-gray-600 text-xs">
                            {language === "zh" ? "AI 驅動" : "AI Powered"}
                        </p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                            <svg
                                className="w-5 h-5 text-purple-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                            </svg>
                        </div>
                        <h4 className="text-gray-900 font-medium text-sm mb-1">
                            {language === "zh" ? "可編輯" : "Editable"}
                        </h4>
                        <p className="text-gray-600 text-xs">
                            {language === "zh"
                                ? "上傳後審核"
                                : "Review after upload"}
                        </p>
                    </div>
                </div>
            </div>

            {/* ── Duplicate Product Modal ─────────────────────────────────── */}
            {duplicateModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                        {/* Header */}
                        <div className="bg-orange-50 px-6 py-4 border-b border-orange-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
                                    <svg
                                        className="w-5 h-5 text-orange-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                                        />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">
                                    {language === "zh"
                                        ? "產品已存在"
                                        : "Product Already Exists"}
                                </h3>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5">
                            <p className="text-gray-700 mb-4">
                                {language === "zh"
                                    ? `名稱為「${duplicateModal.existingProduct?.model_id}」的產品已存在於此分類中。您要為此產品建立新版本嗎？`
                                    : `A product named "${duplicateModal.existingProduct?.model_id}" already exists in this category. Would you like to create a new version of this product?`}
                            </p>

                            {/* Existing product info */}
                            {duplicateModal.existingProduct && (
                                <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1 mb-4">
                                    <p className="text-gray-600">
                                        <span className="font-medium text-gray-800">
                                            {language === "zh"
                                                ? "型號："
                                                : "Model: "}
                                        </span>
                                        {
                                            duplicateModal.existingProduct
                                                .model_id
                                        }
                                    </p>
                                    {duplicateModal.existingProduct
                                        .current_version_label && (
                                        <p className="text-gray-600">
                                            <span className="font-medium text-gray-800">
                                                {language === "zh"
                                                    ? "目前版本："
                                                    : "Current Version: "}
                                            </span>
                                            {
                                                duplicateModal.existingProduct
                                                    .current_version_label
                                            }
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Per-language version summary */}
                            {duplicateModal.versionSummary &&
                                Object.keys(duplicateModal.versionSummary)
                                    .length > 0 && (
                                    <div className="bg-blue-50 rounded-xl p-4 text-sm mb-4">
                                        <p className="font-medium text-gray-800 mb-2">
                                            {language === "zh"
                                                ? "各語言版本數量："
                                                : "Versions per language:"}
                                        </p>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                            {Object.entries(
                                                duplicateModal.versionSummary,
                                            )
                                                .sort(([a], [b]) =>
                                                    a.localeCompare(b),
                                                )
                                                .map(([lang, count]) => (
                                                    <div
                                                        key={lang}
                                                        className="flex justify-between"
                                                    >
                                                        <span className="text-gray-600 uppercase">
                                                            {lang}
                                                        </span>
                                                        <span
                                                            className={`font-mono ${count > 0 ? "text-blue-700 font-semibold" : "text-gray-400"}`}
                                                        >
                                                            {count}
                                                        </span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}

                            {/* Language code dropdown for version naming */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    {language === "zh"
                                        ? "資料表語言"
                                        : "Datasheet Language"}
                                </label>
                                <select
                                    value={selectedLangCode}
                                    onChange={(e) =>
                                        setSelectedLangCode(e.target.value)
                                    }
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                                >
                                    <option value="">
                                        {availableLangCodes.length === 0
                                            ? language === "zh"
                                                ? "載入中..."
                                                : "Loading..."
                                            : language === "zh"
                                              ? "請選擇語言"
                                              : "Select language"}
                                    </option>
                                    {availableLangCodes.map((code, index) => (
                                        <option key={index} value={code.id}>
                                            {code.id} - {code.name}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500">
                                    {language === "zh"
                                        ? "版本標籤將由後端自動產生。"
                                        : "The version label will be auto-generated by the server."}
                                </p>
                            </div>
                        </div>

                        {/* Footer — two buttons */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleNewVersion}
                                className="flex-1 py-2.5 px-4 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                </svg>
                                {language === "zh"
                                    ? "建立新版本"
                                    : "New Version"}
                            </button>
                            <button
                                onClick={handleCancelDuplicate}
                                className="flex-1 py-2.5 px-4 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 font-medium rounded-xl transition-colors"
                            >
                                {language === "zh" ? "取消" : "Cancel"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UploadNew;
